import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Card, Justify, PagingQuery } from 'tea-component';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import SearchRecent from '@/pages/intelligence/components/SearchRecent';
import RPDTable from '@/pages/intelligence/pages/TopCharts/MobileGames/components/Tables/RPDTable';
import useRpdModel, {
  FetchParamsType,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/hooks/useRPDModel';
import Loading from '@/pages/intelligence/components/Loading';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import Visualis from '@/pages/intelligence/pages/TopCharts/MobileGames/components/Visualis/RPDVisualis';
import ChartIconType from '@/pages/intelligence/components/ChartsComponents/ChartIconType';
import { SEGMENT_CHART_SOURCE } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import useRPDBarCharts from '@/pages/intelligence/pages/TopCharts/MobileGames/hooks/useRPDBarCharts';
import useRPDLineCharts from '@/pages/intelligence/pages/TopCharts/MobileGames/hooks/useRPDLineCharts';
import {
  TableSelectStatusType,
  TableSelectionType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { useIntl, getLocale, useSelector, history } from 'umi';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import ExportTable from '../../../../components/ExportTable';
import { fetchMobileTopChartsRpdList } from '@/pages/intelligence/services/topcharts';
import { getFilterName, getFilterValueStorage } from '@/pages/intelligence/components/Filter/util';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import moment from 'moment';
import {
  commonMobileRPDDefaultFilterValue,
  handleRPDFilterParamsToApi,
  stripFetchParamsFilter,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType, getDbiFilterParams } from '@intelligence/utils/filterUtils';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import useMarketFilterApi, {
  MarketSpecailType,
} from '@/pages/intelligence/hooks/useMarketSelector';
import {
  SENSOR_TOWER_TAG_KEYS,
  buildAppMagicTagSpecialParams,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useSenTowerTag from '@/pages/intelligence/hooks/useSenTowerTag';
import useHotTagsOptions from '@intelligence/hooks/useHotTagsOptions';
import useMobileGameEngineOptions from '@intelligence/hooks/useMobileGameEngineOptions';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import { parseUrlFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';
import { GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE } from '@intelligence/components/GenreSelect/constants';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import {
  getTopMobilesRPDCustomValidUrlParams,
  topchartMobileRpdValidCfg,
  hasShortLinkRatio,
} from '@intelligence/_config/urlPageValidCfg';
import {
  getOriginalShortLinkFilter,
  URL_FILTER_KEY,
  updateShortLinkPath,
  URL_CHART_METRIC,
  URL_SHORT_LINK_NAME,
  URL_CHART_TYPE,
  URL_SORT_NAME,
  URL_ORDER,
} from '@intelligence/utils/urlPathCfg';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import { getMobileTopChartsFilterTips } from '@intelligence/pages/TopCharts/MobileGames/_config/mobileTopChartsFilterTips';
import { buildTopChartsTagClickHandler } from '@intelligence/v3/utils/sensorTowerTagColumns';

interface IRPDProps {
  /** TopCharts 主入口显式传递；复用入口不传时沿用 common 作用域。 */
  pageId?: string;
}

export default function RPD(props: IRPDProps) {
  const { pageId } = props;
  const pageLogInfo = dbiPageLogMes.topchartsMobileGamesRPD;
  const entityType = 'mobile';
  const hasUrlShortLink = hasShortLinkRatio.topMobileRpd;
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: 'bar',
    [URL_CHART_METRIC]: 'rpd',
  });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState(null);
  const { filterLoading, regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.rpd,
  });
  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.mobileRpd?.type,
  });
  const { formatMessage, locale } = useIntl();
  const isCn = locale === LANGUAGE_CN;
  const { tagLoading, tagConfig } = useSenTowerTag({});
  const { hotTagsOptions, filterTagName, hotTagsLoading } = useHotTagsOptions('mobile');
  const { loadingStd: definitionsLoading, genreMap, genreOptionsMap, filterTags } =
    useQueryDefinition();
  const { mobileGameEngineOptions } = useMobileGameEngineOptions();

  const [hotTagFilterValue, setHotTagFilterValue] = useState<any>(null);
  const [hotTagResolved, setHotTagResolved] = useState(!filterTagName);

  useEffect(() => {
    if (!filterTagName || hotTagsLoading) return;

    if (!hotTagsOptions.length) {
      setHotTagResolved(true);
      return;
    }

    const matchedOption = hotTagsOptions.find((opt) => opt?.name === filterTagName);
    if (!matchedOption?.value) {
      setHotTagResolved(true);
      return;
    }

    let cancelled = false;
    parseUrlFilterParams(matchedOption.value, { genreOptionsMap })
      .then((parsedValue) => {
        if (cancelled) return;
        if (parsedValue && Object.keys(parsedValue).length > 0) {
          parsedValue[filterKeyMap.HOT_TAGS_SOURCE] = { value: matchedOption };
          setHotTagFilterValue(parsedValue);
        }
        setHotTagResolved(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[RPD] hotTag parseUrlFilterParams failed:', err);
        setHotTagResolved(true);
      });

    return () => {
      cancelled = true;
    };
  }, [filterTagName, hotTagsLoading, hotTagsOptions, genreOptionsMap]);

  const resolvedShortLinkFilterValue = hotTagFilterValue ?? shortLinkFilterValue;

  const appMagicTagConfig = useMemo(
    () => buildAppMagicTagSpecialParams(filterTags, isCn),
    [isCn, filterTags],
  );

  const initPage = async () => {
    setShortLinkLoading(true);
    // 在url上取token，有则请求接口， 判断异常，返回函数值和url上的其他配置参数
    const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();
    if (urlFilter) {
      // 有的筛选器的token时才用后面的参数，没有则不用
      const { params, validQuery } = getTopMobilesRPDCustomValidUrlParams(
        history,
        topchartMobileRpdValidCfg,
      );
      setUrlCustomParams({ ...params });
      setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      setShortLinkFilterValue(urlFilter);
    }
    setShortLinkLoading(false);
  };

  useEffect(() => {
    buriedPointPage(pageLogInfo);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initPage();
  }, []);

  const defaultFilterValue = {
    ...commonMobileRPDDefaultFilterValue,
  };

  const groupedFilter = filterKeyMap.defineFilterGroups(
    [
      {
        key: 'popular',
        titleKey: 'dbi_filter_group_popular',
        filterKeys: [
          filterKeyMap.RANGE_TIME,
          filterKeyMap.REGION,
          filterKeyMap.GENRETOR,
          filterKeyMap.HOT_TAGS_SOURCE,
          filterKeyMap.ALL_Games,
          filterKeyMap.DATASOURCESIM,
          filterKeyMap.RELEASE_TIME,
        ],
      },
      {
        key: 'game_performance',
        titleKey: 'dbi_filter_group_game_performance',
        filterKeys: [filterKeyMap.RPD_RANGE],
      },
      {
        key: 'sensor_tower',
        titleKey: 'dbi_filter_group_sensor_tower',
        iconCode: 'sensortower',
        filterKeys: SENSOR_TOWER_TAG_KEYS,
      },
      {
        key: 'app_magic',
        titleKey: 'dbi_filter_group_app_magic',
        iconCode: 'appmagic',
        filterKeys: filterKeyMap.APPMAGIC_TAG_KEYS,
      },
      {
        key: 'igdb',
        titleKey: 'dbi_filter_group_igdb',
        iconCode: 'igdb',
        filterKeys: [filterKeyMap.GAME_ENGINE],
      },
    ],
    { ungroupedKeys: [filterKeyMap.MY_FILTER] },
  );

  const filterCfg: TopchartFilterCfgType = {
    ...groupedFilter,
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: [moment().subtract(6, 'months'), moment().subtract(1, 'months')],
      },
    },
    specialParams: {
      filterTips: getMobileTopChartsFilterTips(formatMessage),
      myFilterStyle: { width: 140 },
      regionClassName: 'hot-tags-region-select',
      genreClassName: 'hot-tags-genre-select',
      regionOptions, // 国家筛选器options,
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: false,
      rangeTimeType: FilterTimeType.commonRange,
      isStdGenreValuesHandler: true,
      genreOverlayClassName: 'cascade-mode',
      genreOptionsLoading: definitionsLoading,
      genreOptions: genreMap,
      genreSources: GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE,
      disableIegg: false,
      disableAnnie: false,
      disableSensorTower: false,
      sensorTowerTagConfig: tagConfig,
      ...appMagicTagConfig,
      gameEngineOptions: mobileGameEngineOptions,
      hotTagsOptions: hotTagsOptions || [],
      filterTagName,
      globalInfo: formatMessage({ id: `dbi_info_global_filter2` }),
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'current_game' },
      },
      entityType,
    },
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    handleFilterToApiParams: (params: any) => handleRPDFilterParamsToApi(params, entityType),
    hasUrlShortLink,
    enableSticky: true,
  };

  return (
    <RPDComp
      {...props}
      pageId={pageId}
      entityType={entityType}
      pageLogInfo={pageLogInfo}
      filterCfg={filterCfg}
      filterLoading={
        filterLoading
        || tagLoading
        || shortLinkLoading
        || definitionsLoading
        || (!!filterTagName && hotTagsLoading)
        || !hotTagResolved
      }
      rangeTimeComponentDefault={latestTimeTypeHasData}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={resolvedShortLinkFilterValue}
      genreOptionsMap={genreOptionsMap}
    />
  );
}

function RPDComp(props: any) {
  const {
    rangeTimeComponentDefault,
    filterLoading,
    filterCfg,
    pageLogInfo,
    shortLinkValidQuery,
    shortLinkFilterValue,
    urlCustomParams,
    entityType,
    genreOptionsMap,
    pageId,
  } = props;

  const DEFAULT_FORM_VALUE = filterCfg?.defaultValue;
  const regionOptions = filterCfg?.specialParams?.regionOptions || [];

  const filterRef = useRef<any>(null);
  const genreOptionsMapRef = useRef(genreOptionsMap);
  genreOptionsMapRef.current = genreOptionsMap;

  const topChartsType = 'rpd';
  const [tableLoading, setTableLoading] = useState(true);
  const [isVisualisLoading, setIsVisualisLoading] = useState(true);
  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  // 在筛选器这里拿到缓存的值，并渲染
  const filterSaveName = getFilterName(userInfo);
  const filterSaveData = getFilterValueStorage(userInfo);
  // RPD 只读取自己的继承分区，避免被其他 Mobile tab 的时间粒度覆盖。
  const { filterExtend, setFilterExtend, isOperate } = useFilterExtend({ pageId });
  const [filterValue, setFilterVale] = useState(isOperate ? filterExtend : DEFAULT_FORM_VALUE);

  const handleHotTagConfirm = useCallback(async (option: any) => {
    if (!option?.value) return;
    try {
      const parsedValue = await parseUrlFilterParams(option.value, {
        genreOptionsMap: genreOptionsMapRef.current,
      });
      if (parsedValue && Object.keys(parsedValue).length > 0) {
        const nextFilterValue = {
          ...DEFAULT_FORM_VALUE,
          ...parsedValue,
          [filterKeyMap.HOT_TAGS_SOURCE]: { value: option },
        };
        setFilterVale(nextFilterValue);
        filterRef.current?.setFormValue(nextFilterValue);
      }
    } catch (err) {
      console.error('[RPDComp] handleHotTagConfirm failed:', err);
    }
  }, [DEFAULT_FORM_VALUE]);

  const filterCfgWithHotTag = useMemo(
    () => ({
      ...filterCfg,
      specialParams: {
        ...filterCfg?.specialParams,
        onHotTagConfirm: handleHotTagConfirm,
      },
    }),
    [filterCfg, handleHotTagConfirm],
  );

  // echarts图表类型
  const [chartType, setChartType] = useState<ChartType>(
    (shortLinkValidQuery?.[URL_CHART_TYPE] as ChartType) || ChartType.Bar,
  ); // 当前排序指标名
  // 指标value
  const [metric, setMetric] = useState(shortLinkValidQuery?.[URL_CHART_METRIC] || topChartsType);
  const getMetricNameByValue = (metricValue: string) => {
    const metrics = SEGMENT_CHART_SOURCE.RPD || [];
    const metricObj = metrics?.find((item: any) => item.value === metricValue);
    return formatMessage({ id: metricObj?.fullName || metricObj?.text || '' });
  };
  // 指标name
  const metricName = useMemo(() => getMetricNameByValue(metric), [metric, locale]);

  // 是否重新操作过复选器提交
  const [isNewComfirm, setIsNewComfirm] = useState(true);
  // 是否重新勾选过游戏
  const [isNewCheckGame, setIsNewCheckGame] = useState(true);
  // 发布时间最晚时间，发布时间不能选择该日期之后的时间

  const {
    // tableLoading,
    // isVisualisLoading,
    tableData,
    tableTotal,
    barOrPieData,
    setBarOrPieData,
    lineData,
    fetchParams,
    // defaultFetchParams,
    getGameData,
    getLineChartData,
    tableSelection,
    defaultSelectionGames,
    operatedGame,
    setOperatedGame,
    isNodata,
    setTableSelection,
    handleClearSearch,
    transTableDataForSearch,
    srouceValueCur, // 筛选器当前数据源
    gameDetailsLoading,
    gameDetailsMapping,
  } = useRpdModel({
    chartType,
    setChartType,
    shortLinkValidQuery,
    filterValue,
    setFilterVale,
    hasUrlShortLink: filterCfg?.hasUrlShortLink,
    defaultFilterValue: filterCfg.defaultValue,
    setTableLoading,
    setIsVisualisLoading,
  });

  useEffect(() => {
    if (shortLinkValidQuery) {
      if (shortLinkValidQuery[URL_CHART_TYPE] !== undefined) {
        setChartType(shortLinkValidQuery[URL_CHART_TYPE] as ChartType);
      }
      if (shortLinkValidQuery[URL_CHART_METRIC] !== undefined) {
        setMetric(shortLinkValidQuery[URL_CHART_METRIC]);
      }
    }
  }, [shortLinkValidQuery]);

  const sortKeyName = useMemo(
    () => getMetricNameByValue(fetchParams.order_metric),
    [fetchParams?.order_metric],
  );

  const getData = (args: {
    params: FetchParamsType;
    isCheckGame: boolean;
    isSearch?: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues?: any;
    myFilterValue?: string;
  }) => {
    const {
      params,
      isCheckGame,
      isSearch = false,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      myFilterValue = filterSaveName,
    } = args;
    getGameData({
      params: { ...params },
      // params: { ...params, unified_ids: isSearch ? params?.unified_ids : [] },
      isCheckOrSearch: isCheckGame,
      isUpdateLineChart,
      updateFilterValues,
      filterSaveName: myFilterValue || '',
    });
    setIsNewComfirm(true);
    // .then(() => {
    //   setTableLoading(false);
    //   setIsVisualisLoading(false);
    //   setIsNewComfirm(true);
    // })
    // .catch(() => {
    //   setTableLoading(false);
    //   setIsVisualisLoading(false);
    // });
  };

  const getTrend = (params: FetchParamsType, selectionList: TableSelectionType[]) => {
    const idList = selectionList.map((t) => t?.gameId);
    if (!idList?.length) {
      return;
    }
    setIsVisualisLoading(true);
    getLineChartData({ ...params, unified_ids: idList || [] })
      .then(() => {
        setIsVisualisLoading(false);
        setIsNewComfirm(false);
        setIsNewCheckGame(false);
      })
      .catch(() => {
        setIsVisualisLoading(false);
      });
  };

  useEffect(() => {
    setTableLoading(true);
    setIsVisualisLoading(true);
    if (!userInfo?.userid || filterLoading) {
      // 拿缓存值需要拿到用户id
      return;
    }
    let newFetchParams = { ...fetchParams };

    const hasUrlShortLinkVal = history.location?.query?.[URL_SHORT_LINK_NAME];
    const { filterValue: newFilterValues, filterParams } = getDbiFilterParams({
      userInfo,
      urlCustomParams,
      urlShortLinkFilter: shortLinkFilterValue,
      filterSaveData: filterSaveData?.apiValue,
      filterExtend,
      specialParams: filterCfg?.specialParams,
      isOperate,
      filterName: filterSaveName,
      filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
    });

    const newFilterValuesTemp = {
      ...DEFAULT_FORM_VALUE,
      ...newFilterValues,
    };
    // 如果有短链接，设置为继承模式
    if (isOperate || hasUrlShortLinkVal) {
      // 如果目前是继承模式，就设置继承值为过滤后的筛选器值
      setFilterExtend(newFilterValuesTemp);
    }
    setFilterVale(newFilterValuesTemp);
    newFetchParams = {
      ...stripFetchParamsFilter(newFetchParams),
      ...(urlCustomParams || {}),
      ...filterCfg?.handleFilterToApiParams?.(filterParams),
    };
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: newFilterValuesTemp,
    });
  }, [userInfo?.userid, filterLoading, shortLinkFilterValue]);

  useEffect(() => {
    if (!filterLoading && filterCfg?.hasUrlShortLink) {
      // TODO找到它对应的文本， 修改它的名字
      updateShortLinkPath({
        shortLinkQuery: {
          [URL_CHART_TYPE]: chartType,
          [URL_SORT_NAME]: fetchParams.order_metric,
          [URL_ORDER]: fetchParams.order,
          [URL_CHART_METRIC]: metric,
        },
      });
    }
  }, [filterLoading, chartType, fetchParams.order_metric, fetchParams.order, metric]);

  const handleConfirm = (value: any, filter, myFilterValue) => {
    // const result = formatRegionParams(value?.regions);
    const newFormValue = {
      // ...defaultFetchParams,
      ...stripFetchParamsFilter(fetchParams),
      ...value,
      page: 1,
      // page_size: 100,
      page_size: 50,
      /** 确认后重置其他 */
      // order_metric: topChartsType,
      // order: 'desc',
      // search_by_name: '',
      // unified_ids: [],
    };
    // 请求接口
    setTableLoading(true);
    setIsVisualisLoading(true);
    setOperatedGame(false);

    getData({
      params: newFormValue,
      isCheckGame: false,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: filter,
      myFilterValue,
    });
  };

  const onTableSortChage = (e: SortByItemType) => {
    const newFetchParams: FetchParamsType = {
      ...fetchParams,
      order: e?.sortVal,
      order_metric: e?.key,
      page: 1,
      // page_size: 100,
      page_size: 50,
      order_by: [`${e?.key} ${e?.sortVal}`],
    };
    setTableLoading(true);
    if (!operatedGame) {
      /** 没有勾选时点排序则重新Loading柱状图数据 */
      setIsVisualisLoading(true);
    }
    setMetric(newFetchParams?.order_metric);
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: !operatedGame,
    });
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const newFetchParams: FetchParamsType = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      // page_size: e?.pageSize || 100,
      page_size: e?.pageSize || 50,
    };
    setTableLoading(true);
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: false,
    });
  };

  // 表格列表项选择事件回调
  const onTableCheckGame = (
    selectList: TableSelectionType[],
    checkStatus: TableSelectStatusType,
  ) => {
    setIsNewComfirm(true);
    setIsNewCheckGame(true);
    if (chartType === ChartType.Line) {
      getTrend(fetchParams, selectList);
    }
    /** 是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
    } else {
      setOperatedGame(true);
      /** 处理游戏继承 */
      // TODO
    }
    /** 将表格数据转换为柱状或饼 */
    setBarOrPieData(selectList);
    setTableSelection(selectList);
  };
  // 切换charts类型事件
  const handleChartType = (value: ChartType) => {
    if (value === ChartType.Line && tableSelection?.length && isNewComfirm && isNewCheckGame) {
      getTrend(fetchParams, tableSelection);
    }
    setChartType(value);
  };

  // 柱状图相关hooks
  const { seriesData: barSeriesData, barNameList } = useRPDBarCharts(metric, barOrPieData);

  // 折线图相关hooks
  const {
    seriesData: lineSeriesData,
    xAxisData: lineXAxisData,
    yAxisFormatter: lineYAxisFormatter,
    lineLegendsCustomMade,
  } = useRPDLineCharts(metric, lineData, fetchParams?.date_type);

  const getTableSearchList = (id: string) => {
    setTableLoading(true);
    getData({
      params: {
        ...fetchParams,
        unified_ids: [],
        rank_ids: [id],
      },
      isCheckGame: true,
      isSearch: true,
      isUpdateLineChart: false,
    });
  };

  const handleTagClick = useCallback(
    buildTopChartsTagClickHandler({
      filterRef,
      filterValue,
      setFilterValue: setFilterVale,
      sensorTowerTagConfig: filterCfg?.specialParams?.sensorTowerTagConfig,
    }),
    [filterCfg?.specialParams?.sensorTowerTagConfig, filterValue],
  );

  // 转化下载的参数
  const getDownloadFetchParams = useCallback(() => {
    const paramsTmp: {
      [key: string]: any;
    } = {
      ...fetchParams,
      select_ids: tableSelection,
      data_sources_value: srouceValueCur,
      fp: fetchParams,
      regionOptions,
    };
    return paramsTmp;
  }, [fetchParams, srouceValueCur, regionOptions, tableSelection]);

  return (
    <>
      <MarqueeBanner />
      <Card>
        <Card.Body>
          <Filter
            ref={filterRef}
            filterConfirm={handleConfirm}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
            defaultValue={DEFAULT_FORM_VALUE}
            filterCfg={filterCfgWithHotTag}
            extendPageId={pageId}
            rangeTimeComponentDefault={rangeTimeComponentDefault}
            buriedPointParams={pageLogInfo}
          />
        </Card.Body>
      </Card>

      <Card className="dbi-mobileGames-card-context">
        <Card.Body
          title={
            <div style={{ display: 'flex' }}>
              <span style={{ marginRight: 10 }}>
                {formatMessage({ id: `dbi_top_revenue_per_download` })}
              </span>
              <ArrowdownText isNew={true} sortedData={sortKeyName} order={fetchParams.order} />
            </div>
          }
          operation={
            tableLoading ? null : (
              <ChartIconType
                chartType={chartType}
                iconList={['bar', 'line']}
                handleChartType={handleChartType}
              />
            )
          }
        >
          {isVisualisLoading ? (
            <div style={{ height: 402 }}>
              <Loading />
            </div>
          ) : (
            <Visualis
              fetchParams={fetchParams}
              chartType={chartType}
              dateType={fetchParams?.date_type}
              barOrPieData={barOrPieData}
              lineData={lineData}
              isVisualisLoading={isVisualisLoading}
              metric={metric}
              metricName={metricName}
              setMetric={setMetric}
              sortKeyName={sortKeyName}
              sortType={fetchParams.order}
              barSeriesData={barSeriesData}
              isNodata={isNodata}
              lineSeriesData={lineSeriesData}
              lineXAxisData={lineXAxisData}
              legendsCustomMade={lineLegendsCustomMade}
              yAxisFormatter={lineYAxisFormatter}
              // tooltipFormatter={lineTooltipFormatter}
              barNameList={barNameList}
              topChartsType={topChartsType}
              imageType={'mobile'}
              titleNow={metricName}
            />
          )}
        </Card.Body>
      </Card>
      <Justify
        className="dbi-card-justify"
        right={
          <>
            <SearchRecent
              // 传入搜索的filter_ids要为[],不然后端会返回filter_ids里的游戏列表
              fetchParams={{ ...fetchParams, page: 1, filter_ids: [], unified_ids: [], rank_ids: [] }}
              handleClear={handleClearSearch}
              getTableResultList={getTableSearchList}
              searchListApi={fetchMobileTopChartsRpdList}
              pageLogInfo={pageLogInfo}
              searchType={'mobile'}
              initSearchInput={!(fetchParams?.rank_ids?.length > 0)}
              transTableDataForSearch={transTableDataForSearch}
              isUseNewApi={true}
            />
            <ExportTable
              total={tableTotal}
              isDisableDownload={tableLoading}
              businessType="mobile-rpd"
              entityType="mobile"
              tableParams={getDownloadFetchParams()}
              isNeedCheckedLines={true}
              defaultCheckedLines={operatedGame}
              isGameTopCharts={true}
              downloadModalSize={820}
              // pageId={'mobileTopChartsRPD'}
              downloadKey={'mobileTopChartsRPD'}
              useWebDownload={true}
              downloadLogConfigKey={'TopChartsMobileGamesRPDDownloadButton'}
            />
          </>
        }
      />
      <CardSlot>
        <RPDTable
          // className="dbi-mobileGames-table"
          entityType={entityType}
          records={tableData}
          loading={tableLoading}
          tableTotal={tableTotal}
          pageSortAttr={{
            tablePageIndex: fetchParams.page,
            tablePageSize: fetchParams.page_size,
            currentSortValue: fetchParams.order,
            currentSortMetric: fetchParams.order_metric,
          }}
          onTableSortChage={onTableSortChage}
          onTablePagingChange={onTablePagingChange}
          onTableCheckGame={onTableCheckGame}
          asForDate={fetchParams?.end_date}
          tableSelection={tableSelection}
          tableSelectionAll={defaultSelectionGames}
          fetchParams={fetchParams}
          filterValue={filterValue}
          gameDetailsLoading={gameDetailsLoading}
          gameDetailsMapping={gameDetailsMapping}
          onTagClick={handleTagClick}
        />
      </CardSlot>
    </>
  );
}
